import { useEffect, useMemo, useRef, useState } from 'react';
import { useConfirm } from './MailingDialogs';
import { ApiError } from '../api/client';
import { Icon } from '../rondo/marketing/Icon';
import { mailingApi, type CampaignTemplate, type CampaignTemplateBody, type MailingApi } from '../api/mailing';
import { DEFAULT_HTML, VAR_GROUPS, composeBody, sampleVars } from './mailing-content';
import type { MailingPageProps } from './MailingKampanePage';
import { ArcDialog, ArcPage, ArcToolbar } from '../components/arc/primitives/arc-primitives';

type Field = { key: keyof CampaignTemplateBody; label: string; type: 'text' | 'textarea' | 'select'; required?: boolean; rows?: number; code?: boolean; options?: [string, string][]; col?: number };

const FLAG: Record<string, string> = { CZ: '🇨🇿', SK: '🇸🇰', RO: '🇷🇴' };

function fields(countries: readonly string[]): Field[] {
  return [
    { key: 'name', label: 'Název (interní)', type: 'text', required: true, col: 8 },
    { key: 'lang', label: 'Jazyk', type: 'select', options: [['cs', 'CS'], ['sk', 'SK'], ['en', 'EN']], col: 4 },
    { key: 'country', label: 'Trh', type: 'select', options: [['', 'Všechny trhy'], ...countries.map((c) => [c, `${FLAG[c] ?? ''} ${c}`] as [string, string])], col: 4 },
    { key: 'subject', label: 'Předmět e-mailu', type: 'text', col: 8 },
    { key: 'preheader', label: 'Preheader', type: 'text', col: 12 },
    { key: 'bodyHtml', label: 'HTML obsah', type: 'textarea', code: true, rows: 14, required: true, col: 12 },
    { key: 'bodyPlain', label: 'Plain-text fallback', type: 'textarea', rows: 5, col: 12 },
  ];
}

const params = () => new URLSearchParams(window.location.search);
function openWithTemplate(id: number) {
  const path = window.location.pathname.replace(/sablony\/?$/, 'kampane');
  window.location.assign(`${path}?new=1&template=${id}`);
}

function setUrl(next: Record<string, string | null>) {
  const url = new URL(window.location.href);
  for (const [k, v] of Object.entries(next)) { if (v == null) url.searchParams.delete(k); else url.searchParams.set(k, v); }
  window.history.replaceState({}, '', url.toString());
}

export function MailingSablonyPage({ space, eyebrow, countries }: MailingPageProps) {
  const api = useMemo(() => mailingApi(space), [space]);
  const init = params();
  const [editId, setEditId] = useState<number | 'new' | null>(() => {
    if (init.get('new') === '1') return 'new';
    const e = init.get('edit'); return e && !Number.isNaN(Number(e)) ? Number(e) : null;
  });

  return (
    <ArcPage as="div" className="p1-mailing">
      {editId != null
        ? <TemplateEditor api={api} eyebrow={eyebrow} countries={countries} id={editId === 'new' ? null : editId}
            onBack={() => { setEditId(null); setUrl({ edit: null, new: null }); }}
            onCreated={(id) => { setEditId(id); setUrl({ new: null, edit: String(id) }); }} />
        : <Library api={api} eyebrow={eyebrow} countries={countries}
            onEdit={(id) => { setEditId(id); setUrl({ edit: String(id) }); }}
            onNew={() => { setEditId('new'); setUrl({ new: '1' }); }} />}
    </ArcPage>
  );
}

function TemplatePreview({ data }: { data: CampaignTemplateBody }) {
  return (
    <div className="template-message-preview">
      <div className="template-message-meta">
        <div><span>Předmět</span><strong>{sampleVars(data.subject ?? '') || 'Bez předmětu'}</strong></div>
        {data.preheader ? <div><span>Preheader</span><p>{sampleVars(data.preheader)}</p></div> : null}
      </div>
      <div className="template-message-frame">
        <iframe title="mail" sandbox="allow-same-origin" srcDoc={sampleVars(composeBody(data.bodyHtml ?? '', data.preheader ?? ''))} />
      </div>
    </div>
  );
}

function Library({ api, eyebrow, countries, onEdit, onNew }: { api: MailingApi; eyebrow: string; countries: readonly string[]; onEdit: (id: number) => void; onNew: () => void }) {
  const { confirm, dialog } = useConfirm();
  const [list, setList] = useState<CampaignTemplate[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [country, setCountry] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [q, setQ] = useState('');
  const [qDebounced, setQDebounced] = useState('');
  const [preview, setPreview] = useState<CampaignTemplate | null>(null);
  const [count, setCount] = useState<number | null>(null);

  useEffect(() => { const t = setTimeout(() => setQDebounced(q), 300); return () => clearTimeout(t); }, [q]);
  useEffect(() => { api.templateCounts().then((c) => setCount(c.active)).catch(() => undefined); }, [list, api]);

  function reload() {
    setList(null);
    api.templates({ country: country || undefined, all: showArchived, q: qDebounced || undefined })
      .then(setList)
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : 'Načtení selhalo'));
  }
  useEffect(reload, [country, showArchived, qDebounced, api]);

  async function onDuplicate(t: CampaignTemplate) { const d = await api.duplicateTemplate(t.id); onEdit(d.id); }
  async function onArchive(t: CampaignTemplate) { if (!(await confirm({ title: 'Archivovat šablonu?', message: 'Obnovit ji jde přes filtr „Vč. archivovaných“.', confirmLabel: 'Archivovat' }))) return; await api.archiveTemplate(t.id); reload(); }
  async function onRestore(t: CampaignTemplate) { await api.restoreTemplate(t.id); reload(); }

  const filtersActive = country !== '' || showArchived || q.trim().length > 0;

  return (
    <ArcPage className="mailing-template-library">
      <header className="prototype-page-head mailing-template-page-head">
        <div><div className="prototype-eyebrow">{eyebrow}</div><h1>Šablony</h1><p>Hotové e-maily připravené pro další kampaň</p></div>
        <button type="button" className="mailing-template-primary" onClick={onNew}><Icon name="plus" size={16} />Nová šablona</button>
      </header>

      <ArcToolbar className="mailing-template-toolbar" label="Filtry šablon" end={(
        <div className="mailing-template-search" role="search">
          <Icon name="search" size={17} />
          <input type="search" aria-label="Hledat šablonu" placeholder="Hledat název nebo předmět…" value={q} onChange={(e) => setQ(e.target.value)} />
          {q && <button type="button" onClick={() => setQ('')} aria-label="Vymazat hledání"><Icon name="x" size={14} /></button>}
        </div>
      )}>
        <div className="mailing-template-market">
          <span>Trh</span>
          <div role="group" aria-label="Vybrat trh">
            {['', ...countries].map((c) => (
              <button key={c || 'all'} type="button" className={country === c ? 'active' : ''} aria-pressed={country === c} onClick={() => setCountry(c)}>{c || 'Vše'}</button>
            ))}
          </div>
        </div>
        <button type="button" className={`mailing-template-archive-toggle${showArchived ? ' active' : ''}`} aria-pressed={showArchived} onClick={() => setShowArchived((v) => !v)}>
          <Icon name="archive" size={14} />Včetně archivu
        </button>
        <button type="button" className="mailing-template-reset" disabled={!filtersActive} onClick={() => { setCountry(''); setShowArchived(false); setQ(''); }}>Vyčistit</button>
      </ArcToolbar>

      <div className="mailing-template-list-meta" aria-live="polite">
        <span>{!list ? 'Načítám šablony…' : `${list.length} ${list.length === 1 ? 'šablona' : list.length > 1 && list.length < 5 ? 'šablony' : 'šablon'}`}</span>
        {count != null && <span><strong>{count}</strong> aktivních v knihovně</span>}
      </div>

      {error ? <div className="mailing-template-message is-error"><Icon name="alert-circle" size={17} />{error}</div>
        : !list ? <div className="prototype-page-loading" aria-label="Načítám"><span /><span /><span /><span /></div>
        : list.length === 0 ? (
          <div className="mailing-template-empty">
            <span><Icon name="mail" size={21} /></span><h2>Žádná šablona neodpovídá filtrům</h2><p>Uprav filtry, nebo připrav novou e-mailovou šablonu.</p>
            <button type="button" onClick={onNew}>Nová šablona</button>
          </div>
        ) : (
          <div className="template-library-grid">
            {list.map((t) => (
              <article className={`template-library-card${t.enabled ? '' : ' is-archived'}`} key={t.id}>
                <div className="template-card-body">
                  <div className="template-card-kicker"><span>{t.country ? `${FLAG[t.country] ?? ''} ${t.country}` : 'Všechny trhy'}</span><code>{t.lang}</code>{!t.enabled && <em>Archiv</em>}</div>
                  <h2>{t.name}</h2>
                  <p>{t.subject || 'Bez výchozího předmětu'}</p>
                  <div className="template-card-meta"><span>Upraveno {t.createdAt ? new Date(t.createdAt).toLocaleDateString('cs-CZ') : '—'}</span>{t.authorName && <span>{t.authorName}</span>}</div>
                  <div className="template-card-actions">
                    {t.enabled && <button className="is-primary" type="button" onClick={() => openWithTemplate(t.id)}><Icon name="send" size={14} />Použít</button>}
                    <button className="is-icon" type="button" title="Náhled" aria-label={`Náhled ${t.name}`} onClick={() => setPreview(t)}><Icon name="eye" size={14} /></button>
                    <button type="button" onClick={() => onEdit(t.id)}><Icon name="edit-2" size={14} />Upravit</button>
                    <span className="template-card-utilities">
                      <button type="button" title="Duplikovat" aria-label={`Duplikovat ${t.name}`} onClick={() => onDuplicate(t)}><Icon name="copy" size={14} /></button>
                      {!t.enabled
                        ? <button className="is-restore" type="button" title="Obnovit" aria-label={`Obnovit ${t.name}`} onClick={() => onRestore(t)}><Icon name="rotate-ccw" size={14} /></button>
                        : <button className="is-archive" type="button" title="Archivovat" aria-label={`Archivovat ${t.name}`} onClick={() => onArchive(t)}><Icon name="archive" size={14} /></button>}
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

      {preview && (
        <ArcDialog
          title={preview.name}
          description={`E-mail · ${preview.country || 'všechny trhy'} · ${String(preview.lang).toUpperCase()}`}
          onClose={() => setPreview(null)}
          size="wide"
          actions={<>
            <button type="button" onClick={() => setPreview(null)}>Zavřít</button>
            <button type="button" onClick={() => { const id = preview.id; setPreview(null); onEdit(id); }}><Icon name="edit-2" size={14} />Upravit šablonu</button>
            {preview.enabled && <button className="is-primary" type="button" onClick={() => openWithTemplate(preview.id)}><Icon name="send" size={14} />Použít v kampani</button>}
          </>}
        >
          <div className="template-preview-canvas"><TemplatePreview data={preview} /></div>
        </ArcDialog>
      )}
      {dialog}
    </ArcPage>
  );
}

function TemplateEditor({ api, eyebrow, countries, id, onBack, onCreated }: {
  api: MailingApi; eyebrow: string; countries: readonly string[]; id: number | null; onBack: () => void; onCreated: (id: number) => void;
}) {
  const { confirm, dialog } = useConfirm();
  const schema = useMemo(() => fields(countries), [countries]);
  const [data, setData] = useState<CampaignTemplateBody>(id == null ? { lang: 'cs', country: null, bodyHtml: DEFAULT_HTML, enabled: true } : {});
  const [loading, setLoading] = useState(id != null);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [pk, setPk] = useState(0);
  const archived = id != null && data.enabled === false;
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const htmlRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (id == null) return;
    setLoading(true);
    api.template(id).then((d) => setData(d)).catch((e: unknown) => setErr(e instanceof ApiError ? e.message : 'Načtení selhalo')).finally(() => setLoading(false));
  }, [id, api]);

  function set<K extends keyof CampaignTemplateBody>(k: K, v: CampaignTemplateBody[K]) { setData((d) => ({ ...d, [k]: v })); setSaved(false); }

  function insertVar(token: string) {
    const ta = htmlRef.current;
    const html = String(data.bodyHtml ?? '');
    if (!ta) { set('bodyHtml', html + token); return; }
    const start = ta.selectionStart ?? html.length;
    const end = ta.selectionEnd ?? html.length;
    set('bodyHtml', html.slice(0, start) + token + html.slice(end));
    requestAnimationFrame(() => { ta.focus(); ta.selectionStart = ta.selectionEnd = start + token.length; });
  }

  async function save() {
    const missing = schema.filter((f) => f.required && (!data[f.key] || String(data[f.key]).trim() === ''));
    if (missing.length) { setErr(`Vyplň povinná pole: ${missing.map((m) => m.label).join(', ')}`); return; }
    setSaving(true); setErr(null);
    try {
      const body: CampaignTemplateBody = {
        name: data.name, lang: data.lang ?? 'cs', country: data.country || null, subject: data.subject ?? null,
        preheader: data.preheader ?? null, bodyHtml: data.bodyHtml ?? null, bodyPlain: data.bodyPlain ?? null,
      };
      if (id == null) {
        const r = await api.createTemplate({ ...body, enabled: true });
        onCreated(r.id);
      } else {
        await api.updateTemplate(id, body);
        setSaved(true);
        if (savedTimer.current) clearTimeout(savedTimer.current);
        savedTimer.current = setTimeout(() => setSaved(false), 1500);
      }
    } catch (e) { setErr(e instanceof ApiError ? e.message : 'Uložení selhalo'); }
    finally { setSaving(false); }
  }
  async function doArchive() { if (id == null) return; if (!(await confirm({ title: 'Archivovat šablonu?', message: 'Obnovit ji jde přes filtr „Vč. archivovaných“.', confirmLabel: 'Archivovat' }))) return; await api.archiveTemplate(id); onBack(); }
  async function doRestore() { if (id == null) return; await api.restoreTemplate(id); onBack(); }

  if (loading) return <div className="prototype-page-loading" aria-label="Načítám"><span /><span /><span /><span /></div>;

  return (
    <ArcPage className="template-editor-page">
      <button type="button" className="template-back" onClick={onBack}><Icon name="arrow-left" size={14} />Zpět do knihovny</button>
      <header className="template-editor-head">
        <div>
          <span>{eyebrow} · ŠABLONY</span><h1>{id == null ? 'Nová e-mail šablona' : String(data.name ?? '')}</h1>
          <div className="template-editor-meta">
            <span>E-mail</span>
            {data.country ? <span>{FLAG[String(data.country)] ?? ''} {String(data.country)}</span> : <span>Všechny trhy</span>}
            {data.lang ? <span>{String(data.lang).toUpperCase()}</span> : null}
            {archived && <span className="is-archived">Archiv</span>}
          </div>
        </div>
        <div className="template-editor-actions">
          {id != null && !archived && <button className="is-danger" type="button" onClick={doArchive}><Icon name="archive" size={14} />Archivovat</button>}
          {id != null && archived && <button type="button" onClick={doRestore}><Icon name="rotate-ccw" size={14} />Obnovit</button>}
          {id != null && !archived && <button type="button" onClick={() => openWithTemplate(id)}><Icon name="send" size={14} />Použít</button>}
          <button className="is-primary" type="button" onClick={save} disabled={saving}>{saved ? '✓ Uloženo' : saving ? 'Ukládám…' : 'Uložit změny'}</button>
        </div>
      </header>
      {err && <div className="mailing-template-message is-error"><Icon name="alert-circle" size={17} />{err}</div>}

      <div className="template-editor-workspace">
        <div className="template-editor-form">
          <div className="campaign-step-head"><span>Obsah</span><h2>Údaje šablony</h2><p>Název slouží uvnitř P1. Předmět a obsah uvidí příjemce kampaně.</p></div>
            <div className="row g-3">
              {schema.map((f) => (
                <div key={f.key} className={`col-${f.col ?? 12} template-editor-field`}>
                  <label>{f.label}{f.required && <span> *</span>}</label>
                  {f.key === 'bodyHtml' && (
                    <div className="template-variable-toolbar">
                      <span>Vložit proměnnou</span>
                      {VAR_GROUPS.flatMap((g) => g.vars).map((v) => (
                        <button key={v.token} type="button" onClick={() => insertVar(v.token)}>{v.text}</button>
                      ))}
                    </div>
                  )}
                  {f.type === 'textarea'
                    ? <textarea ref={f.key === 'bodyHtml' ? htmlRef : undefined} className={f.code ? 'is-code' : ''} rows={f.rows ?? 4} value={String(data[f.key] ?? '')} onChange={(e) => set(f.key, e.target.value as never)} />
                    : f.type === 'select'
                      ? <select value={String(data[f.key] ?? (f.options?.[0]?.[0] ?? ''))} onChange={(e) => set(f.key, (f.key === 'country' && !e.target.value ? null : e.target.value) as never)}>{(f.options ?? []).map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
                      : <input type="text" value={String(data[f.key] ?? '')} onChange={(e) => set(f.key, e.target.value as never)} />}
                </div>
              ))}
            </div>
        </div>
        <aside className="template-editor-preview">
            <div className="template-editor-preview-head">
              <div><span>ŽIVÝ NÁHLED</span><strong>E-mail příjemce</strong></div>
              <button type="button" onClick={() => setPk((k) => k + 1)}><Icon name="refresh-cw" size={13} />Obnovit</button>
            </div>
            <TemplatePreview key={pk} data={data} />
        </aside>
      </div>
      {dialog}
    </ArcPage>
  );
}
